import React, { useState } from 'react';
import { CartItem, UserAddress, ShippingMethod, PaymentMethod } from '../../types';
import { useAuth } from '../AuthProvider';
import { saveUserDocument } from '../../services/supabaseService';
import { useNotification } from '../member/NotificationProvider';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeStoreGroup: {
    id: string | number;
    name: string;
    items: CartItem[];
    total: number;
  } | null;
  addresses: UserAddress[];
  selectedAddressId: string | number | null;
  shippingMethods: ShippingMethod[];
  selectedShippingId: number | null;
  paymentMethods: PaymentMethod[];
  selectedPaymentId: number | string | null;
  selectedStoreFreightPrice: number;
  processedRates: any[];
  hasDocument: boolean | null;
  setHasDocument: (val: boolean) => void;
  isOrderingFromOwnStore?: boolean;
  myStore?: any;
  deliveryMode?: 'pickup' | 'delivery';
  storeInfo?: any;
  onConfirmOrder: () => Promise<void>;
  isProcessing: boolean;
}

const formatWeight = (grams: number | null | undefined): string => {
  const g = grams || 0;
  if (g >= 1000) {
    return `${(g / 1000).toFixed(2)} kg`;
  }
  return `${g.toFixed(0)}g`;
};

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  activeStoreGroup,
  addresses,
  selectedAddressId,
  shippingMethods,
  selectedShippingId,
  paymentMethods,
  selectedPaymentId,
  selectedStoreFreightPrice,
  processedRates,
  hasDocument,
  setHasDocument,
  isOrderingFromOwnStore = false,
  myStore,
  deliveryMode = 'pickup',
  storeInfo,
  onConfirmOrder,
  isProcessing
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [userCpf, setUserCpf] = useState('');
  const [isSavingDoc, setIsSavingDoc] = useState(false);

  if (!isOpen) return null;

  const handleSaveCpf = async () => {
    if (!user || userCpf.length < 11) return;
    setIsSavingDoc(true);
    try {
      await saveUserDocument(user.id, userCpf);
      setHasDocument(true);
      showNotification('Documento registrado com sucesso!', 'success');
    } catch {
      showNotification('Erro ao salvar documento. Tente novamente.', 'error');
    } finally {
      setIsSavingDoc(false);
    }
  };

  const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId);
  const selectedShipping = shippingMethods.find((m) => m.id === selectedShippingId);
  const selectedPayment = paymentMethods.find((m) => m.id === selectedPaymentId);

  const isPickup = isOrderingFromOwnStore || deliveryMode === 'pickup' || !selectedShippingId || (selectedShipping?.name?.toLowerCase().includes('retirada') ?? false);
  const effectiveFreightPrice = isPickup ? 0 : selectedStoreFreightPrice;

  const deliveryDays = selectedShippingId
    ? processedRates.find(
        (r) => Number(r.id) === Number(selectedShipping?.external_id)
      )?.delivery_time || selectedShipping?.estimated_days || 5
    : 5;

  const totalOrderAmount = (activeStoreGroup?.total || 0) + effectiveFreightPrice;
  const totalWeightGrams = activeStoreGroup?.items.reduce(
    (acc: number, item: any) => acc + (Number(item.weight) || 0) * (item.quantity || 1),
    0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80">
      <div 
        className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div>
            <h3 className="text-base sm:text-lg font-black text-white uppercase tracking-wider">
              Confirmação do Pedido
            </h3>
            <p className="text-xs text-slate-400">
              Revise suas informações para confirmar
            </p>
          </div>
          <button
            type="button"
            disabled={isProcessing}
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors cursor-pointer text-sm"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
          {hasDocument === null && !isOrderingFromOwnStore ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-8 h-8 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
              <p className="text-xs font-bold text-slate-400">Verificando dados da conta...</p>
            </div>
          ) : hasDocument === false && !isOrderingFromOwnStore ? (
            <div className="space-y-5">
              <div className="bg-purple-950/40 border border-purple-500/30 p-5 rounded-2xl space-y-3">
                <div className="w-10 h-10 bg-purple-600/20 rounded-xl flex items-center justify-center text-purple-400 text-lg">
                  <i className="fas fa-id-card"></i>
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm">Documento Necessário</h4>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    Para garantir a segurança das transações na plataforma, precisamos vincular um CPF à sua conta para finalizar pedidos.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300 block">Seu CPF</label>
                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={userCpf}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 11) setUserCpf(val);
                  }}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 focus:border-purple-500 transition-colors outline-none font-mono text-sm tracking-widest text-center"
                />
                <p className="text-[10px] text-slate-500 italic">Suas informações são protegidas e criptografadas.</p>
              </div>

              <button
                type="button"
                disabled={userCpf.length < 11 || isSavingDoc || isProcessing}
                onClick={handleSaveCpf}
                className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider py-3.5 rounded-xl transition-colors cursor-pointer"
              >
                {isSavingDoc ? 'Processando...' : 'Confirmar e Continuar'}
              </button>
            </div>
          ) : (
            <div className="space-y-3.5">
              <p className="text-xs text-slate-400 leading-normal">
                {isOrderingFromOwnStore
                  ? 'Revise os dados da venda no balcão antes de confirmar o pedido.'
                  : 'Por favor, revise os dados da compra antes de prosseguir para o pagamento seguro.'}
              </p>

              {/* Loja */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-3.5 space-y-1">
                <span className="text-[10px] font-mono uppercase text-purple-400 font-bold block">Loja Vendedora</span>
                <div className="flex items-center space-x-2 text-xs font-bold text-white">
                  <i className="fas fa-store text-slate-400 text-xs"></i>
                  <span>
                    {activeStoreGroup?.name} {isOrderingFromOwnStore && '(Sua Loja)'}
                  </span>
                </div>
              </div>

              {/* Endereço / Ponto de Retirada */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-3.5 space-y-1">
                <span className="text-[10px] font-mono uppercase text-purple-400 font-bold block">
                  {isPickup ? 'Ponto de Retirada (Loja Física)' : 'Endereço de Entrega'}
                </span>
                {isPickup ? (
                  <div className="text-xs text-slate-300">
                    <p className="font-bold text-white">{storeInfo?.name || myStore?.name || activeStoreGroup?.name || 'Loja Vendedora'}</p>
                    <p className="text-slate-400 mt-0.5">
                      {storeInfo?.street 
                        ? `${storeInfo.street}${storeInfo.number ? `, ${storeInfo.number}` : ', S/N'}${storeInfo.neighborhood ? ` - ${storeInfo.neighborhood}` : ''}${storeInfo.city ? `, ${storeInfo.city}` : ''}${storeInfo.state?.code || storeInfo.estado ? ` - ${storeInfo.state?.code || storeInfo.estado}` : ''}`
                        : myStore?.street 
                          ? `${myStore.street}, ${myStore.number || 'S/N'}` 
                          : 'Balcão da Loja Física'}
                    </p>
                    <span className="text-emerald-400 font-semibold text-[10px] block mt-1">
                      <i className="fas fa-store text-[9px] mr-1"></i>
                      Retirada presencial no balcão da loja após confirmação
                    </span>
                  </div>
                ) : selectedAddress ? (
                  <div className="text-xs text-slate-300">
                    <p className="font-bold text-white">
                      {selectedAddress.street}, {selectedAddress.number}{' '}
                      {selectedAddress.complement && `- ${selectedAddress.complement}`}
                    </p>
                    <p className="text-slate-400 mt-0.5">
                      {selectedAddress.neighborhood}, {selectedAddress.city} - {selectedAddress.state}
                    </p>
                    <p className="font-mono text-[10px] text-slate-500 mt-0.5">CEP: {selectedAddress.cep}</p>
                  </div>
                ) : (
                  <p className="text-xs text-rose-400">Nenhum endereço selecionado.</p>
                )}
              </div>

              {/* Envio / Retirada */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-3.5 space-y-1">
                <span className="text-[10px] font-mono uppercase text-purple-400 font-bold block">
                  {isPickup ? 'Forma de Retirada' : 'Opção de Envio'}
                </span>
                {isPickup ? (
                  <div className="flex justify-between items-center text-xs">
                    <div>
                      <p className="font-bold text-white">Retirada no Balcão da Loja</p>
                      <p className="text-[10px] text-emerald-400">Sem custo de frete (Retirada Imediata)</p>
                    </div>
                    <span className="font-mono font-bold text-emerald-400 text-xs">Grátis</span>
                  </div>
                ) : selectedShipping ? (
                  <div className="flex justify-between items-center text-xs">
                    <div>
                      <p className="font-bold text-white">{selectedShipping.name}</p>
                      <p className="text-[10px] text-slate-400">Prazo: {deliveryDays} dias úteis</p>
                    </div>
                    <span className="font-mono font-bold text-emerald-400 text-xs">
                      R$ {selectedStoreFreightPrice.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-rose-400">Nenhum frete selecionado.</p>
                )}
              </div>

              {/* Pagamento */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-3.5 space-y-1">
                <span className="text-[10px] font-mono uppercase text-purple-400 font-bold block">Meio de Pagamento</span>
                {isOrderingFromOwnStore ? (
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-white">Pagamento em Dinheiro (Balcão)</span>
                    <i className="fas fa-money-bill-1-wave text-emerald-400 text-sm"></i>
                  </div>
                ) : selectedPayment ? (
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <i className="fas fa-circle-check text-emerald-400 text-xs"></i>
                      <span className="font-bold text-white">{selectedPayment.name}</span>
                    </div>
                    <i
                      className={`fas ${
                        selectedPayment.type === 'pix' || selectedPayment.slug === 'pix'
                          ? 'fa-pix text-emerald-400'
                          : (selectedPayment.slug === 'cash' || selectedPayment.slug === 'dinheiro' || selectedPayment.requires_online === false || selectedPayment.name?.toLowerCase().includes('dinheiro'))
                            ? 'fa-money-bill-1-wave text-amber-400'
                            : 'fa-credit-card text-purple-400'
                      } text-sm`}
                    ></i>
                  </div>
                ) : (
                  <p className="text-xs text-rose-400">Nenhum pagamento selecionado.</p>
                )}
              </div>

              {/* Lista compacta de Itens */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-3.5 space-y-2">
                <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                  Itens do Pedido ({activeStoreGroup?.items.length})
                </span>
                <div className="max-h-28 overflow-y-auto space-y-1.5 pr-1 text-xs divide-y divide-slate-800/60">
                  {activeStoreGroup?.items.map((item: any, idx: number) => (
                    <div key={`modal-item-${idx}`} className="flex items-center justify-between pt-1 first:pt-0">
                      <div className="truncate max-w-[75%]">
                        <span className="text-slate-500 font-mono">({item.quantity}x) </span>
                        <span className="text-slate-300 font-medium truncate">{item.name}</span>
                      </div>
                      <span className="text-white font-mono text-xs">
                        R$ {((Number(item.price) || 0) * (item.quantity || 1)).toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Resumo de Valores */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Produtos</span>
                  <span className="font-mono text-slate-200">
                    R$ {activeStoreGroup?.total.toFixed(2).replace('.', ',')}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Peso Total</span>
                  <span className="font-mono text-slate-200">{formatWeight(totalWeightGrams)}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Envio / Entrega</span>
                  <span className="font-mono text-slate-200">
                    {isPickup ? 'Grátis (Retirada na Loja)' : `R$ ${selectedStoreFreightPrice.toFixed(2).replace('.', ',')}`}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-black text-white pt-2.5 border-t border-slate-800">
                  <span>TOTAL DO PEDIDO</span>
                  <span className="text-purple-400 font-mono text-base font-extrabold">
                    R$ {totalOrderAmount.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(hasDocument || isOrderingFromOwnStore) && (
          <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-950 flex items-center space-x-3">
            <button
              type="button"
              disabled={isProcessing}
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer"
            >
              Voltar
            </button>
            <button
              type="button"
              disabled={(!selectedPaymentId && !isOrderingFromOwnStore) || isProcessing}
              onClick={onConfirmOrder}
              className={`flex-1 text-white font-black text-xs uppercase tracking-wider py-3 rounded-xl transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                isOrderingFromOwnStore
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : 'bg-purple-600 hover:bg-purple-500'
              }`}
            >
              <span>
                {isProcessing
                  ? 'Processando...'
                  : isOrderingFromOwnStore
                  ? 'Confirmar Pedido Balcão'
                  : 'Pagar Agora'}
              </span>
              {!isProcessing && (
                <i className={`fas ${isOrderingFromOwnStore ? 'fa-check' : 'fa-lock'} text-xs`}></i>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
